import { FC, PropsWithChildren, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import classes from './dropdown.module.scss'

interface IProps {
    title: ReactNode;
}

const VIEWPORT_GAP = 20

export const Dropdown: FC<IProps & PropsWithChildren> = ({title, children}) => {

    const [open, setOpen] = useState<boolean>(false)

    const refContainer = useRef<HTMLDivElement>(null)
    const refContent = useRef<HTMLDivElement>(null)
    const requestAnimationFrameId = useRef<number | null>(null)

    const choicePosition = useCallback(() => {
        if(!refContainer.current || !refContent.current) return

        const container = refContainer.current
        const content = refContent.current

        content.style.left = ''
        content.style.right = '0'
        const containerRect = container.getBoundingClientRect()
        const boundaryRect = container.closest('[data-dropdown-boundary]')?.getBoundingClientRect()
        const boundaryLeft = boundaryRect?.left ?? 0
        const boundaryRight = boundaryRect?.right ?? window.innerWidth
        const maxWidth = Math.max(0, boundaryRight - boundaryLeft - VIEWPORT_GAP * 2)
        content.style.maxWidth = `${maxWidth}px`
        const contentRect = content.getBoundingClientRect()

        const nextLeft = Math.min(
            Math.max(contentRect.left, boundaryLeft + VIEWPORT_GAP),
            boundaryRight - contentRect.width - VIEWPORT_GAP,
        )

        content.style.left = `${nextLeft - containerRect.left}px`
        content.style.right = 'auto'
    }, [])
    
    const clickDoc = useCallback((e: MouseEvent) => {
        const target = e.target as Element;
        if((refContainer.current && refContainer.current.contains(target)) || target.closest('.modal')){}
        else{
            setOpen(false)
            document.removeEventListener('click', clickDoc, {capture: true})
        }
    }, [])

    useEffect(() => {
        return () => {
            document.removeEventListener('click', clickDoc, {capture: true})
        }
    }, [clickDoc])  

    const onClick = () => {
        if(open){
            setOpen(false)
            document.removeEventListener('click', clickDoc, {capture: true})
        }
        else{
            document.addEventListener('click', clickDoc, {capture: true})
            setOpen(true)
        }
    }

    useLayoutEffect(() => {
        if(!open) return

        choicePosition()
    }, [choicePosition, open])

    useEffect(() => {
        if(!open || !refContent.current || typeof ResizeObserver === 'undefined') return

        const observer = new ResizeObserver(() => {
            if(requestAnimationFrameId.current) cancelAnimationFrame(requestAnimationFrameId.current)
            requestAnimationFrameId.current = requestAnimationFrame(choicePosition)
        })
        observer.observe(refContent.current)

        return () => {
            observer.disconnect()
            if(requestAnimationFrameId.current) cancelAnimationFrame(requestAnimationFrameId.current)
        }
    }, [choicePosition, open])

    useEffect(() => {
        if(!open) return

        let timePrev = Date.now()

        const onResize = () => {
            const timeCur = Date.now()
            if(timeCur - timePrev > 60){
                timePrev = timeCur;

                requestAnimationFrameId.current = requestAnimationFrame(choicePosition)
            }

        }

        window.addEventListener('resize', onResize)

        return () => {
            window.removeEventListener('resize', onResize)
            if(requestAnimationFrameId.current)
                cancelAnimationFrame(requestAnimationFrameId.current)
        }
    }, [choicePosition, open])

    return (
        <section 
            ref={refContainer} 
            className={classes.container + (open ? ` ${classes.open}` : '')}
        >
            <section 
                onClick={onClick} 
                className={classes.title}
                onMouseDown={e => e.preventDefault()}
            >
                {title}
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M26 12L16 22L6 12" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>     
            </section>        
            <section 
                className={classes.dropdown}
                ref={refContent}
            >
                {children}
            </section>
        </section>
    )
}
